import { COMPANY_ACTIVE_SINCE } from "../assets/constants";
import type { DateISO, LocaleCode } from "~/types";
import dayjs from 'dayjs';

const LEGAL_AGE = 18;

export function nowDate() {
  return now().toDate();
}

export function getDaysAheadIso(n: number) {
  return toIso(dayjs().add(n, 'day'));
}

export function isPast(date: DateISO) {
  return dayjs(date).isBefore(now(), 'day');
}

export function isoToDate(iso: DateISO) {
  return parseIso(iso).toDate();
}

export function toStartOfDayIso(date: DateISO) {
  return parseIso(date).startOf('day').toISOString();
}

export function toEndOfDayIso(date: DateISO) {
  return parseIso(date).endOf('day').toISOString();
}

export function dateToIso(date: Date) {
  return toIso(dayjs(date));
}

export function getTodayIso() {
  return toIso(now());
}

export function isoToTime(iso: DateISO) {
  return dayjs(iso).valueOf();
}

export function nowTime() {
  return now().valueOf();
}

export function isMoreThanDaysAgo(date: DateISO, days: number) {
  return dayjs(date).isBefore(dayjs().subtract(days, 'day'));
}

export function daysFromToday(date: DateISO) {
  return dayjs(date).diff(now(), 'day');
}

export function isAdult(dateOfBirth: DateISO) {
  return calcAge(dateOfBirth) >= LEGAL_AGE;
}

export function getYearsAheadIso(years: number) {
  const date = now().add(years, 'year');
  return toIso(date);
}

export function getYearsAgoIso(years: number) {
  const date = now().subtract(years, 'year');
  return toIso(date);
}

export function lastYear() {
  return now().subtract(1, 'year').year();
}

export function getDaysAgoDate(days: number) {
  return now().subtract(days, 'day').startOf('day').toDate();
}

export function getDaysAgoFullIso(days: number) {
  return now().subtract(days, 'day').toISOString();
}

export function getTodayStart() {
  return now().startOf('day').toDate();
}

export function getHoursAgoIso(hours: number) {
  return now().subtract(hours, 'hour').toISOString();
}

export function isBefore(date1: string, date2: string) {
  return dayjs(date1).isBefore(date2);
}

export function isAfter(date1: string, date2: string) {
  return dayjs(date1).isAfter(date2);
}

export function calcYearDifference(year: number) {
  return Math.abs(now().year() - year);
}

export function getCurrentYear() {
  return now().year();
}

export function calcCompanyActiveYears() {
  return calcYearDifference(COMPANY_ACTIVE_SINCE) + 1;
}

export function isSameDay(date1: Date, date2: Date) {
  return dayjs(date1).isSame(date2, 'day');
}

export function calcAge(dateOfBirth: DateISO) {
  return now().diff(dayjs(dateOfBirth), 'year');
}

function parseIso(iso: DateISO) {
  if (iso.includes('T')) return dayjs(iso);
  const [year, month, day] = isoToDayMonthYear(iso);
  return dayjs().year(year).month(month - 1).date(day);
}

function toIso(dayJs: dayjs.Dayjs): DateISO {
  return dayJs.format('YYYY-MM-DD');
}

function isoToDayMonthYear(iso: DateISO) {
  return iso.split('T')[0].split('-').map(Number);
}

export function isoToShortMonthDate(iso: DateISO, locale: LocaleCode) {
  const date = dayjs(iso).locale(locale);
  const day = date.format("DD");
  const month = date.format("MMM").toUpperCase();
  const year = date.format("YYYY");
  return `${day}/${month}/${year}`;
}

function now() {
  return dayjs();
}